import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AdminPasswordIssue } from '@harbormaster/shared';
import { ADMIN_PASSWORD_MIN_LENGTH, getAdminPasswordIssue } from '@harbormaster/shared';
import { supabase } from '../lib/supabase';
import { tAdminPasswordIssue } from '../lib/passwordValidation';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

const COUNTRIES = [
  'Estonia', 'Finland', 'Sweden', 'Norway', 'Denmark', 'Latvia', 'Lithuania',
  'Germany', 'Netherlands', 'United Kingdom', 'France', 'Spain', 'Italy',
  'Poland', 'Croatia', 'Greece', 'Portugal', 'Belgium', 'Austria', 'Switzerland',
  'United States', 'Canada', 'Australia', 'New Zealand', 'Other',
];

export function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    marinaName: '',
    yourName: '',
    email: '',
    country: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.marinaName.trim()) next.marinaName = t('auth.marinaNameRequired');
    if (!form.yourName.trim()) next.yourName = t('auth.yourNameRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = t('auth.enterValidEmail');
    if (!form.country) next.country = t('auth.selectCountryError');
    const pwdIssue = getAdminPasswordIssue(form.password);
    if (pwdIssue) next.password = tAdminPasswordIssue(t, pwdIssue);
    if (form.password !== form.confirmPassword) next.confirmPassword = t('auth.passwordsDoNotMatch');
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setServerError('');

    try {
      const res = await fetch(`${API_BASE}/api/v1/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          marinaName: form.marinaName.trim(),
          yourName: form.yourName.trim(),
          email: form.email.toLowerCase().trim(),
          country: form.country,
          password: form.password,
        }),
      });

      const data = (await res.json()) as {
        success?: boolean;
        session?: { access_token: string; refresh_token: string } | null;
        error?: string;
        code?: string;
        issue?: AdminPasswordIssue;
      };

      if (!res.ok || !data.success) {
        if (data.code === 'EMAIL_EXISTS') {
          setErrors({ email: t('auth.emailExists') });
        } else if (data.code === 'INVALID_PASSWORD' && data.issue) {
          setErrors({ password: tAdminPasswordIssue(t, data.issue) });
        } else {
          setServerError(data.error ?? t('auth.signupFailed'));
        }
        return;
      }

      if (data.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      navigate('/onboarding');
    } catch {
      setServerError(t('auth.signupNetworkError'));
    } finally {
      setSubmitting(false);
    }
  }

  function field(name: keyof typeof form) {
    return {
      value: form[name],
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setForm((prev) => ({ ...prev, [name]: e.target.value })),
    };
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-600 mb-3">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{t('auth.createYourMarina')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('auth.trialNoCard')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {serverError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.marinaName')}</label>
              <input
                type="text"
                placeholder={t('auth.marinaNamePlaceholder')}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.marinaName ? 'border-red-400' : 'border-gray-300'}`}
                {...field('marinaName')}
              />
              {errors.marinaName && <p className="text-xs text-red-600 mt-1">{errors.marinaName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.yourNameLabel')}</label>
              <input
                type="text"
                placeholder={t('auth.yourNamePlaceholder')}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.yourName ? 'border-red-400' : 'border-gray-300'}`}
                {...field('yourName')}
              />
              {errors.yourName && <p className="text-xs text-red-600 mt-1">{errors.yourName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
              <input
                type="email"
                placeholder="you@marina.com"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                {...field('email')}
              />
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.country')}</label>
              <select
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${errors.country ? 'border-red-400' : 'border-gray-300'}`}
                {...field('country')}
              >
                <option value="">{t('auth.selectCountry')}</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {errors.country && <p className="text-xs text-red-600 mt-1">{errors.country}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.password')}</label>
              <input
                type="password"
                placeholder={t('auth.passwordPlaceholder')}
                minLength={ADMIN_PASSWORD_MIN_LENGTH}
                autoComplete="new-password"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-400' : 'border-gray-300'}`}
                {...field('password')}
              />
              <p className="text-xs text-gray-500 mt-1">{t('auth.passwordRequirementsHint')}</p>
              {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')}</label>
              <input
                type="password"
                placeholder={t('auth.confirmPasswordPlaceholder')}
                minLength={ADMIN_PASSWORD_MIN_LENGTH}
                autoComplete="new-password"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.confirmPassword ? 'border-red-400' : 'border-gray-300'}`}
                {...field('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-2 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? t('auth.creatingAccount') : t('auth.createFreeAccount')}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-gray-500">
            {t('auth.termsAgreement')}{' '}
            <a href="#" className="text-blue-600 hover:underline">{t('auth.termsOfService')}</a>
            {' '}{t('auth.and')}{' '}
            <a href="#" className="text-blue-600 hover:underline">{t('auth.privacyPolicy')}</a>.
          </p>
        </div>

        <p className="mt-4 text-center text-sm text-gray-600">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link to="/login" className="text-blue-600 font-medium hover:underline">
            {t('auth.logIn')}
          </Link>
        </p>
      </div>
    </div>
  );
}
